import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Admin Auth
export const adminLogin = async (username, password) => {
  try {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    localStorage.setItem('admin_token', response.data.access_token);
    localStorage.setItem('admin_username', response.data.username);
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const verifyAdminToken = async () => {
  try {
    const response = await axios.get(`${API}/admin/verify`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const adminLogout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_username');
};

export const isAdminLoggedIn = () => {
  return !!localStorage.getItem('admin_token');
};

// Admin Settings API
export const adminUpdateSettings = async (settingsData) => {
  try {
    const response = await axios.put(`${API}/settings`, settingsData, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

// Admin Gallery API
export const adminCreateGalleryImage = async (imageData) => {
  try {
    const response = await axios.post(`${API}/gallery`, imageData, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating gallery image:', error);
    throw error;
  }
};

export const adminUpdateGalleryImage = async (imageId, imageData) => {
  try {
    const response = await axios.put(`${API}/gallery/${imageId}`, imageData, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating gallery image:', error);
    throw error;
  }
};

export const adminDeleteGalleryImage = async (imageId) => {
  try {
    const response = await axios.delete(`${API}/gallery/${imageId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting gallery image:', error);
    throw error;
  }
};

// Admin Bulk Delete Gallery API
export const adminBulkDeleteGalleryImages = async (ids) => {
  const response = await axios.post(`${API}/gallery/bulk-delete`, { ids }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin Gallery Reorder API
export const adminReorderGalleryImages = async (items) => {
  try {
    const response = await axios.put(`${API}/gallery/reorder`, { items }, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error reordering gallery images:', error);
    throw error;
  }
};

// Admin Contact API
export const adminGetContactMessages = async (status = null) => {
  try {
    const url = status ? `${API}/contact?status=${status}` : `${API}/contact`;
    const response = await axios.get(url, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching contact messages:', error);
    throw error;
  }
};

export const adminUpdateContactMessageStatus = async (messageId, status) => {
  const response = await axios.patch(`${API}/contact/${messageId}`, { status }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminGetContactExportUrl = () => {
  const token = localStorage.getItem('admin_token');
  return `${API}/contact/export.csv?auth=${encodeURIComponent(token || '')}`;
};

export const adminDownloadContactCsv = async () => {
  const response = await axios.get(`${API}/contact/export.csv`, {
    headers: getAuthHeader(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `contact_messages_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Admin Stats API
export const adminGetHeroStats = async () => {
  try {
    const response = await axios.get(`${API}/admin/hero-stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching hero stats:', error);
    throw error;
  }
};

export const adminGetStats = async (days = 30) => {
  try {
    const response = await axios.get(`${API}/admin/stats?days=${days}`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    throw error;
  }
};

// Admin file upload (object storage)
export const adminUploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/upload`, formData, {
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'multipart/form-data',
    },
  });
  // Backend returns { url: '/api/files/<path>', ... } — prepend backend host
  return {
    ...response.data,
    full_url: `${BACKEND_URL}${response.data.url}`,
  };
};